import { MetaQuery } from '@refinedev/core';
import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from './axios';

class GatepassOrderApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/wms/gatepassorder');
  }

  protected transformNode(node: any) {
    // Transform gatepass order data if needed
    return node;
  }

  async findById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    const response = await axiosInstance.get(
      `${this.baseURL}/${id}/item-details`,
      this.getRequestConfig(meta),
    );
    return this.transformNode(response.data.data) as T;
  }

  // Custom methods for gatepass order specific operations
  async findByFacility(facilityId: string) {
    return axiosInstance.get(`${this.baseURL}/orders/facility/${facilityId}`);
  }

  async getGatepassOrderSummary(id: string) {
    return axiosInstance.get(`${this.baseURL}/${id}/summary`);
  }

  // Handle close action
  async close(sorters: any, filters: any, payload: any, query: any, headers: any, meta: any) {
    if (meta?.gatepassOrderId) {
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.gatepassOrderId}/close`,
        payload,
      );
      return response.data;
    }
    throw new Error('gatepassOrderId is required for close action');
  }

  // Handle link-gatepass action
  async 'link-gatepass'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassOrderId && (meta?.gatepassId || payload?.gatepassCode)) {
      // If gatepassId is provided, use the original endpoint
      if (meta?.gatepassId) {
        const response = await axiosInstance.post(
          `${this.baseURL}/${meta.gatepassOrderId}/link-gatepass/${meta.gatepassId}`,
        );
        return response.data;
      }
      // If gatepassCode is provided, use the new endpoint that accepts gatepass code
      if (payload?.gatepassCode) {
        const response = await axiosInstance.post(
          `${this.baseURL}/${meta.gatepassOrderId}/link-gatepass`,
          {
            gatepassCode: payload.gatepassCode,
          },
        );
        return response.data;
      }
    }
    throw new Error(
      'gatepassOrderId and either gatepassId or gatepassCode are required for link-gatepass action',
    );
  }

  // Handle unlinked-gatepasses action
  async 'unlinked-gatepasses'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    const response = await axiosInstance.get(`${this.baseURL}/unlinked-gatepasses`);
    return response.data;
  }

  // Handle force-activate action (force release leg-2 DRAFT GPO)
  async 'force-activate'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassOrderId) {
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.gatepassOrderId}/force-activate`,
      );
      return response.data;
    }
    throw new Error('gatepassOrderId is required for force-activate action');
  }

  // Handle mark-in-transit action
  async 'mark-in-transit'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassOrderId) {
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.gatepassOrderId}/mark-in-transit`,
        payload,
      );
      return response.data;
    }
    throw new Error('gatepassOrderId is required for mark-in-transit action');
  }

  // Handle reversal-preview action (dry-run: eligibility + line items, no mutation)
  async 'reversal-preview'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassOrderId) {
      const response = await axiosInstance.get(
        `${this.baseURL}/${meta.gatepassOrderId}/reversal/preview`,
      );
      return response.data;
    }
    throw new Error('gatepassOrderId is required for reversal-preview action');
  }

  // Handle reversal-create action (creates + auto-approves the reversal STN)
  async 'reversal-create'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.gatepassOrderId) {
      const response = await axiosInstance.post(
        `${this.baseURL}/${meta.gatepassOrderId}/reversal`,
      );
      return response.data;
    }
    throw new Error('gatepassOrderId is required for reversal-create action');
  }
}

export default GatepassOrderApiClient;
